import { updateUser, useUser } from '@gik/api/users/user';
import { optimisticUpdate } from '@gik/core/api';
import { User } from '@gik/core/models/gik/User';
import { useUserStore } from '@gik/core/store/UserStore';
import { Analytics } from '@gik/core/utils';
import { UI, FormSchemaEntry, Form } from '@gik/ui';
import React from 'react';
import { translationKeys } from './i18n/en';
import { useTranslation } from 'react-i18next';

export type JITRequirement = 'user-firstName'; // TODO: add other requirements here

/**
 * Just in time data collection for requirements
 *
 * @param requirements array of <b>memoized</b> requirements to be collected
 * @param options customize the behavior of the JIT Collection
 * @returns function to launch a modal to collect any requirements.
 * - If requirements are not yet loaded, null will be returned.
 * - The function will return true if all missing data is provided by the user.
 * - If no requirements are missing, the function will immediately return true.
 */
export default function useJITCollect(
  requirements: JITRequirement[],
  options: {
    closable: boolean;
    initiatedOn: string;
  } = {
    closable: false,
    initiatedOn: 'appload',
  }
): () => Promise<boolean> | null {
  const { t } = useTranslation();
  const userId = useUserStore(store => store.id);
  const requiresFirstName = requirements.indexOf('user-firstName') >= 0;

  // only retrieve user for firstName requirement, other data for other checks can be added below
  const { data: user, mutate: mutateUser } = useUser(requiresFirstName ? userId : null);

  let isLoading = false;
  let areRequirementsMissing = false;

  // TODO: add a better way to manage title and description
  let title = t(translationKeys.defaultTitle);
  let description = t(translationKeys.defaultDescription);

  // individual requirements missing
  let isFirstNameRequirementMissing = false;

  // check if first name is missing, other checks can be added below
  if (requiresFirstName) {
    if (user) {
      if (!user.firstName) {
        isFirstNameRequirementMissing = true;
        areRequirementsMissing = true;
        title = t(translationKeys.completeUserProfileTitle);
        description = t(translationKeys.completeUserProfileDescription);
      }
    } else {
      isLoading = true;
    }
  }

  // TODO: if we need multiple schemas, consider adding a multi-step modal.

  // ensure the schema is only generated once based on which requirements are missing
  const memoizedSchema: FormSchemaEntry[] = React.useMemo(() => {
    const schema: FormSchemaEntry[] = [];
    // TODO: take from user settings
    if (isFirstNameRequirementMissing) {
      // TODO: remove duplication between here, signup form and user settings
      schema.push({
        name: 'firstName',
        type: 'text',
        minLength: 2,
        maxLength: 127,
        required: true,
        props: {
          placeholder: 'Your First name',
        },
      });
    }
    // TODO: add checks for other requirements here

    return schema;
  }, [isFirstNameRequirementMissing]);

  const launchModal = React.useCallback(() => {
    if (areRequirementsMissing && !isLoading) {
      Analytics.fireEvent('JITDataCollectionModalOpened', {
        requirements: requirements.join(','),
        initiatedOn: options.initiatedOn,
      });
      return UI.dialog(
        ({ close }) => {
          // Note: values are unknown as it is based on the schema generated by the missing requirements
          async function handleSubmit(values: unknown) {
            try {
              if (isFirstNameRequirementMissing) {
                const firstNameFormValues = values as { firstName: string };
                const updatedUser: User = {
                  ...user,
                  ...firstNameFormValues,
                };
                // TODO: use PATCH instead of PUT and only update user's first name rather than the full object
                await optimisticUpdate(updatedUser, mutateUser, () => updateUser(userId, updatedUser));
                //updateUser
              }
              close(true);
            } catch (error) {
              console.error(error);
              // TODO: better error message
              UI.alert(t(translationKeys.error));
            }
          }
          return (
            <>
              {description ? <p>{description}</p> : null}
              <Form variant="primary" vertical schema={memoizedSchema} onSubmit={handleSubmit} submitButton />
            </>
          );
        },
        { closable: options.closable, title }
      );
    } else {
      return Promise.resolve(true);
    }
  }, [
    areRequirementsMissing,
    isLoading,
    options.closable,
    options.initiatedOn,
    memoizedSchema,
    title,
    description,
    requirements,
    mutateUser,
    user,
    isFirstNameRequirementMissing,
    userId,
    t,
  ]);

  return isLoading ? null : launchModal;
}
